import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SecUsuarioService } from '../../../services/database/secUsuario.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { AlertIcon, AlertService, CustomAlertComponent, FormGroupErrorMessageComponent, LoadingPanelService, noWhitespaceValidator, PASSWORD_PATTERN, Utils } from 'lib-base';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { RestablecerPasswordRequest } from '../../../shared/interfaces/database/seguridad';

@Component({
  selector: 'app-restablecer-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, FormGroupErrorMessageComponent, PasswordModule, ButtonModule, DividerModule, CustomAlertComponent],
  templateUrl: './restablecer-password.component.html',
  styleUrl: './restablecer-password.component.scss'
})
export class RestablecerPasswordComponent implements OnInit, OnDestroy {
  private _codigo !: string;
  isCodeValid: boolean = false;

  form !: FormGroup;
  currentTime !: Date;
  location!: string;
  translations: any = {};
  private _hideDialog !: Subscription;

  constructor(private _route: ActivatedRoute, private _pu: SecUsuarioService, private _r: Router, private _fb: FormBuilder,
    private _t: TranslateService, private _ms: MessageService, private _lps: LoadingPanelService, private _as: AlertService,
    private _router: Router) {
    this._codigo = this._route.snapshot.params['codigo'];
  }

  ngOnInit(): void {
    this.translate();
    this.isCodigoValido();
    this.updateLocation();
    this.updateTime();
  }

  ngOnDestroy(): void {
    if (this._hideDialog) {
      this._hideDialog.unsubscribe();
    }
  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      codigoNoValido: this._t.get('components.app-restablecer-password.codigo-no-valido'),
      siguiente: this._t.get('form.button.siguiente'),
      contrasena: this._t.get('form.password.contrasena'),
      constasenaNoCoiniciden: this._t.get('form.password.constasena-no-coiniciden'),
      repitaContrasena: this._t.get('form.password.repita-contraseña'),
      alMenosUnaMinuscula: this._t.get('form.password.al-menos-una-minuscula'),
      alMenosUnaMayuscula: this._t.get('form.password.al-menos-una-mayuscula'),
      alMenosUnNumero: this._t.get('form.password.al-menos-un-número'),
      minimoCaracteres: this._t.get('form.password.minimo-caracteres'),
      maximoCaracteres: this._t.get('form.password.maximo-caracteres'),
      alMenosUnCaracterEspecial: this._t.get('form.password.al-menos-un-caracter-especial'),
      cambiarContrasena: this._t.get('form.password.cambiar-contrasena'),
      contrasenaCambiadaTitle: this._t.get('components.app-restablecer-password.contrasena-cambiada-title'),
      contrasenaCambiadaHtml: this._t.get('components.app-restablecer-password.contrasena-cambiada-html'),
      completeCampos: this._t.get('form.msg.complete-campos'),
      warnSummary: this._t.get('form.msg.warn-summary'),
      welcome: this._t.get('common.bienvenido')
    }));
  }

  configureForm() {
    this.form = this._fb.group({
      password: [null, [Validators.required, noWhitespaceValidator, Validators.pattern(PASSWORD_PATTERN), Validators.maxLength(200)]],
      passwordRepeat: [null, [Validators.required, noWhitespaceValidator]],
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    return control.value.password === control.value.passwordRepeat ? null : { notMatch: true };
  }

  onSubmit() {
    if (this.form.invalid) {
      Utils.markAllFormControlsAsTouched(this.form);
      this._ms.add({ severity: 'warn', summary: this.translations.warnSummary, detail: this.translations.completeCampos });
      return;
    }

    let request = {
      codigo: this._codigo,
      newPassword: this.form.get('password')?.value
    } as RestablecerPasswordRequest;
    this.restablecerPassword(request);
  }


  updateTime(): void {
    this.currentTime = new Date();
  }

  updateLocation(): void {
    const now = new Date();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = -now.getTimezoneOffset() / 60;
    const offsetString = offset >= 0 ? `+${offset}:00` : `${offset}:00`;
    this.location = `${timeZone} ${offsetString}`;
  }
  //#region API Calls
  isCodigoValido() {
    this._lps.show(true);
    this._pu.isCodigoRestablecerPasswordValido(this._codigo).subscribe({
      next: (response: boolean) => {
        this._lps.hide();
        this.isCodeValid = response;
        if (response) {
          this.configureForm();
          return;
        }
        this._hideDialog = this._as.show({
          icon: AlertIcon.WARN,
          title: this.translations.cambiarContrasena,
          text: this.translations.codigoNoValido,
          showAccept: true,
          textAccept: this.translations.siguiente,
        }).subscribe(key => {
          this._router.navigate(['public/login']);
        });
      }
    });
  }

  restablecerPassword(request: RestablecerPasswordRequest) {
    this._lps.show();
    this._pu.restablecerPassword(request).subscribe({
      next: (response: string) => {
        this._lps.hide();
        this._hideDialog = this._as.show({
          icon: AlertIcon.SUCCESS,
          title: this.translations.contrasenaCambiadaTitle,
          text: this.translations.contrasenaCambiadaHtml,
          showAccept: true,
          textAccept: this.translations.siguiente,
        }).subscribe(key => {
          this._router.navigate(['public/login']);
        });
      }
    });
  }
  //#endregion
}
